import { useUser } from "@clerk/clerk-react";
import { useAuth } from "@clerk/clerk-react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FileUp,
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  Maximize,
  X,
  Paperclip,
  Save,
  AlertTriangle,
  Moon,
  Sun,
  Calendar,
  Clock,
  Download,
  FileText,
  BookOpen,
  Command,
  Sparkles,
  PenTool,
  User,
} from "lucide-react";
import ChatWidget from "./ChatWidget";
import WelcomeMessage from "./WelcomeMessage";
import Navbar from "../components/Navbar";

const url = process.env.REACT_APP_BACK_END_URL;

const getFileIcon = (filename) => {
  const ext = filename.split(".").pop().toLowerCase();
  const icons = {
    pdf: <FileText className="text-red-500" size={20} />,
    doc: <FileText className="text-blue-500" size={20} />,
    docx: <FileText className="text-blue-500" size={20} />,
    xls: <FileText className="text-green-500" size={20} />,
    xlsx: <FileText className="text-green-500" size={20} />,
  };
  
  return icons[ext] || <Paperclip className="text-gray-500 dark:text-gray-400" size={20} />;
};

// Custom Card Components with modern styling
const Card = ({ children, className = "", expandable = false }) => (
  <div
    className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 dark:border-gray-700 ${
      expandable ? "transform hover:scale-[1.01] hover:-translate-y-1" : ""
    } ${className}`}
  >
    {children}
  </div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`p-6 ${className}`}>{children}</div>
);

const IconButton = ({ 
  icon, 
  onClick, 
  className = "", 
  tooltip = "", 
  variant = "default",
  ...props 
}) => {
  const variants = {
    default: "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700",
    secondary: "text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700",
    danger: "text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400",
  };

  return (
    <button
      onClick={onClick}
      className={`p-2 rounded-full transition-all duration-200 flex items-center justify-center ${variants[variant]} ${className}`}
      {...props}
      title={tooltip}
    >
      {icon}
    </button>
  );
};

// Button component with enhanced variants and animations
const Button = ({
  children,
  onClick,
  variant = "primary",
  size = "md",
  icon = null,
  className = "",
  loading = false,
  ...props
}) => {
  const baseClasses = "font-medium rounded-lg transition-all duration-200 flex items-center gap-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 dark:focus:ring-offset-gray-800";

  const variantClasses = {
    primary: "bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white dark:from-indigo-500 dark:to-violet-500 dark:hover:from-indigo-600 dark:hover:to-violet-600",
    secondary: "bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600",
    danger: "bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800",
    ghost: "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300",
    success: "bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800",
  };

  const sizeClasses = {
    sm: "text-sm px-3 py-1.5",
    md: "px-4 py-2",
    lg: "text-lg px-5 py-2.5",
    xl: "text-xl px-6 py-3",
  };

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${loading ? 'opacity-70 cursor-not-allowed' : ''} ${className}`}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading ? (
        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
      ) : icon && icon}
      {children}
    </button>
  );
};

// FileUploadPreview component with enhanced styling
const FileUploadPreview = ({ file, onRemove }) => {
  return (
    <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg group hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 border border-gray-200 dark:border-gray-700">
      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-white dark:bg-gray-700 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600">
        {getFileIcon(file.name)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="truncate font-medium text-gray-800 dark:text-gray-200">{file.name}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {(file.size / 1024 / 1024).toFixed(2)} MB
        </p>
      </div>
      <button
        onClick={onRemove}
        className="p-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
        aria-label="Remove file"
      >
        <X size={16} />
      </button>
    </div>
  );
};

const NotesAurora = () => {
  // State declarations
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState("default");
  const [currentNote, setCurrentNote] = useState({
    id: null,
    title: "",
    content: "",
    attachments: [],
  });
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [newPageTitle, setNewPageTitle] = useState("");
  const [isAddingPage, setIsAddingPage] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [editingPageId, setEditingPageId] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const quillRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('aurora-dark-mode');
    if (savedMode !== null) {
      return savedMode === 'true';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('aurora-dark-mode', darkMode);
  }, [darkMode]);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => !prev);
  }, []);

  const { user } = useUser();
  const userId = user ? user.id : null;
  const { getToken } = useAuth();

  // ------------------------------------------
  // Toast Utility (for quick messages)
  // ------------------------------------------
  const toast = (message, type = "info") => {
    // Simple placeholder; replace with your own toast library in production
    alert(`${type.toUpperCase()}: ${message}`);
  };

  // ------------------------------------------
  // Fetch Pages
  // ------------------------------------------
  useEffect(() => {
    fetchPages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPages = async () => {
    setIsLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(url + "/api/pages", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Sort pages by createdAt descending
        const sortedPages = data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setPages(sortedPages);
      } else {
        toast("Failed to fetch pages", "error");
      }
    } catch (error) {
      console.error("Error:", error);
      toast("Error fetching pages", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Derived data for the current page
  const currentPageData = pages.find((page) => page._id === currentPage) || { notes: [] };

  // Sort and filter notes
  const sortedNotes = currentPageData.notes
    ?.slice()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const filteredNotes = sortedNotes?.filter(
    (note) =>
      (note.title && note.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (note.content && note.content.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // ------------------------------------------
  // File Upload Handler
  // ------------------------------------------
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);

    const validFiles = files.filter((file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      const validExtensions = ["pdf", "doc", "docx", "xls", "xlsx"];
      const maxSize = 10 * 1024 * 1024; // 10MB limit

      if (!validExtensions.includes(ext)) {
        toast("Invalid file type. Only PDF, Word, and Excel files are allowed.", "error");
        return false;
      }
      if (file.size > maxSize) {
        toast(`File too large (${file.name}). Max size is 10MB.`, "error");
        return false;
      }
      return true;
    });

    setAttachments((prev) => [...prev, ...validFiles]);
  };

  // Remove a file from the new attachments list
  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // ------------------------------------------
  // Delete Note
  // ------------------------------------------
  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;

    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/notes/${noteId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        // Remove note from local state
        setPages(
          pages.map((page) => {
            if (page._id === currentPage) {
              return {
                ...page,
                notes: page.notes.filter((note) => note._id !== noteId),
              };
            }
            return page;
          })
        );
        toast("Note deleted successfully", "success");
      } else {
        toast("Failed to delete note", "error");
      }
    } catch (error) {
      console.error("Error deleting note:", error);
      toast("Error deleting note", "error");
    }
  };

  // ------------------------------------------
  // Delete Page
  // ------------------------------------------
  const handleDeletePage = async (pageId) => {
    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/pages/${pageId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const updatedPages = pages.filter((page) => page._id !== pageId);
        setPages(updatedPages);

        if (updatedPages.length > 0) {
          setCurrentPage(updatedPages[0]._id);
        } else {
          setCurrentPage("default");
        }
        toast("Page deleted successfully", "success");
      } else {
        toast("Failed to delete page", "error");
      }
    } catch (error) {
      console.error("Error deleting page:", error);
      toast("Error deleting page", "error");
    }
  };

  // ------------------------------------------
  // Add Page
  // ------------------------------------------
  const handleAddPage = async () => {
    if (!newPageTitle.trim()) return;
    setIsLoading(true);

    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/pages`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId, title: newPageTitle }),
      });

      if (response.ok) {
        const newPage = await response.json();
        await fetchPages(); // refresh pages
        setCurrentPage(newPage._id);
        setNewPageTitle("");
        setIsAddingPage(false);
        toast("Page created successfully", "success");
      } else {
        toast("Failed to add page", "error");
      }
    } catch (error) {
      console.error("Error adding page:", error);
      toast("Error adding page", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------------------------------
  // Rename Page
  // ------------------------------------------
  const handleRenamePage = async (pageId, newTitle) => {
    if (!newTitle.trim()) return;

    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/pages/${pageId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ title: newTitle }),
      });

      if (response.ok) {
        const updatedPage = await response.json();
        setPages(
          pages.map((page) => (page._id === pageId ? { ...page, title: updatedPage.title } : page))
        );
        setEditingPageId(null);
        toast("Page renamed successfully", "success");
      } else {
        toast("Failed to update page title", "error");
      }
    } catch (error) {
      console.error("Error updating page title:", error);
      toast("Error updating page title", "error");
    }
  };

  // ------------------------------------------
  // Save New Note
  // ------------------------------------------
  const handleSave = async () => {
    if (!currentNote.title || !currentNote.content) {
      toast("Title and content are required.", "error");
      return;
    }

    setIsLoading(true);
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("pageId", currentPage);
      formData.append("title", currentNote.title);
      formData.append("content", currentNote.content);
      formData.append("format", "html");

      if (attachments && attachments.length > 0) {
        attachments.forEach((file) => {
          formData.append("attachments", file);
        });
      }

      const response = await fetch(`${url}/api/notes`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        const savedNote = await response.json();
        // Add the new note to local state
        setPages(
          pages.map((page) => {
            if (page._id === currentPage) {
              return { ...page, notes: [...page.notes, savedNote] };
            }
            return page;
          })
        );
        setCurrentNote({ id: null, title: "", content: "", attachments: [] });
        setAttachments([]);
        setIsEditing(false);
        toast("Note saved successfully", "success");
      } else {
        const errorData = await response.json();
        toast(`Failed to save note: ${errorData.error || "Unknown error"}`, "error");
      }
    } catch (error) {
      console.error("Error saving note:", error);
      toast("Error saving note. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------------------------------
  // Update Existing Note
  // ------------------------------------------
  const handleUpdate = async (noteId) => {
    if (!currentNote.title || !currentNote.content) {
      toast("Title and content are required.", "error");
      return;
    }

    setIsLoading(true);
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append("title", currentNote.title);
      formData.append("content", currentNote.content);
      formData.append("format", "html");

      if (attachments && attachments.length > 0) {
        attachments.forEach((file) => {
          formData.append("attachments", file);
        });
      }

      const response = await fetch(`${url}/api/notes/${noteId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        const updatedNote = await response.json();
        // Update the existing note in local state
        setPages(
          pages.map((page) => {
            if (page._id === currentPage) {
              return {
                ...page,
                notes: page.notes.map((note) => (note._id === noteId ? updatedNote : note)),
              };
            }
            return page;
          })
        );
        setCurrentNote({ id: null, title: "", content: "", attachments: [] });
        setAttachments([]);
        setIsEditing(false);
        toast("Note updated successfully", "success");
      } else {
        const errorData = await response.json();
        toast(`Failed to update note: ${errorData.error || "Unknown error"}`, "error");
      }
    } catch (error) {
      console.error("Error updating note:", error);
      toast("Error updating note. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // ------------------------------------------
  // Delete Attachment (for existing note)
  // ------------------------------------------
  const handleDeleteAttachment = async (noteId, filename) => {
    if (!window.confirm("Delete this attachment?")) return;

    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/notes/${noteId}/attachments/${filename}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        // Remove attachment from local state
        setPages((prevPages) =>
          prevPages.map((page) => {
            if (page._id === currentPage) {
              return {
                ...page,
                notes: page.notes.map((note) => {
                  if (note._id === noteId) {
                    return {
                      ...note,
                      attachments: note.attachments.filter(
                        (attach) => attach.filename !== filename
                      ),
                    };
                  }
                  return note;
                }),
              };
            }
            return page;
          })
        );

        // If we are currently editing that note, remove from currentNote as well
        if (currentNote._id === noteId) {
          setCurrentNote((prev) => ({
            ...prev,
            attachments: prev.attachments.filter(
              (attachment) => attachment.filename !== filename
            ),
          }));
        }

        toast("Attachment deleted successfully", "success");
      } else {
        const errorData = await response.json();
        toast(`Failed to delete attachment: ${errorData.error || "Unknown error"}`, "error");
      }
    } catch (error) {
      console.error("Error deleting attachment:", error);
      toast("Error deleting attachment. Please try again.", "error");
    }
  };

  // ------------------------------------------
  // Note Content Component
  // ------------------------------------------
  const NoteContent = ({ noteId, content, attachments, onDeleteAttachment }) => {
    const renderAttachments = () => {
      if (!attachments || attachments.length === 0) return null;

      return (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h3 className="text-sm font-semibold mb-2 text-gray-700 flex items-center gap-2">
            <Paperclip size={14} /> Attachments
          </h3>
          <div className="space-y-2">
            {attachments.map((file, index) => {
              const fileIcon = getFileIcon(file.originalName);
              return (
                <div
                  key={index}
                  className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all group"
                >
                  <span className="text-xl">{fileIcon}</span>
                  <a
                    href={`${url}/uploads/${file.filename}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 text-indigo-600 hover:text-indigo-800 truncate font-medium"
                    download={file.originalName}
                  >
                    {file.originalName}
                  </a>
                  <span className="text-sm text-gray-500">
                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                  {onDeleteAttachment && (
                    <button
                      onClick={() => onDeleteAttachment(noteId, file.filename)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <div>
        <div
          className="prose prose-sm max-w-none
            prose-headings:font-bold prose-headings:text-gray-900
            prose-p:text-gray-700 prose-p:leading-relaxed
            prose-a:text-indigo-600 prose-a:no-underline hover:prose-a:underline
            prose-strong:text-gray-900 prose-strong:font-semibold
            [&_pre]:overflow-x-auto 
            [&_pre]:max-w-full 
            [&_pre]:p-4 
            [&_pre]:bg-gray-50 
            [&_pre]:rounded-lg 
            [&_pre]:my-2 
            [&_code]:text-sm 
            [&_code]:font-mono 
            [&_pre]:border 
            [&_pre]:border-gray-200
            [&_ul]:list-disc 
            [&_ul]:pl-4 
            [&_ol]:list-decimal 
            [&_ol]:pl-4 
            [&_li]:my-1"
          dangerouslySetInnerHTML={{ __html: content }}
        />
        {renderAttachments()}
      </div>
    );
  };

  // ------------------------------------------
  // Loading Spinner
  // ------------------------------------------
  const LoadingSpinner = () => (
    <div className="flex justify-center items-center p-8">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 dark:border-gray-700 border-t-indigo-600 dark:border-t-indigo-400 shadow-sm"></div>
    </div>
  );

  // ------------------------------------------
  // Empty State
  // ------------------------------------------
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center p-12 text-center">
      <div className="bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20 p-6 rounded-full mb-6 shadow-sm">
        <BookOpen size={36} className="text-indigo-600 dark:text-indigo-400" />
      </div>
      <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">No notes found</h3>
      <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-md">Get started by creating your first note</p>
      <Button
        onClick={() => {
          setIsEditing(true);
          setCurrentNote({
            id: null,
            title: "",
            content: "",
            attachments: [],
          });
        }}
        icon={<Sparkles size={18} />}
        className="shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 px-6 py-2.5"
      >
        Create Your First Note
      </Button>
    </div>
  );

  // ------------------------------------------
  // Main Return
  // ------------------------------------------
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col transition-colors duration-200">
      {/* Navbar */}
      <Navbar />

      <div className="flex flex-1">
        {/* Sidebar toggle button */}
        {!isSidebarOpen && (
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="fixed left-4 top-20 z-20 bg-white dark:bg-gray-800 p-2 rounded-full shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-700"
            aria-label="Open sidebar"
          >
            <ChevronRight size={20} />
          </button>
        )}
        
        {/* Dark mode toggle button */}
        <button
          onClick={toggleDarkMode}
          className="fixed right-4 top-20 z-20 bg-white dark:bg-gray-800 p-2 rounded-full shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-700"
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
        >
          {darkMode ? <Sun size={20} /> : <Moon size={20} />}
        </button>

        {/* Sidebar */}
        <div
          className={`${
            isSidebarOpen ? "w-72" : "w-0"
          } transition-all duration-300 bg-white dark:bg-gray-800 h-screen border-r border-gray-100 dark:border-gray-700 overflow-hidden relative shadow-sm`}
        >
          <div className="p-6 h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">Pages</h2>
              <div className="flex gap-2">
                <IconButton
                  icon={<Plus size={18} />}
                  onClick={() => setIsAddingPage(true)}
                  variant="secondary"
                  tooltip="Add page"
                />
                <IconButton
                  icon={<ChevronLeft size={18} />}
                  onClick={() => setIsSidebarOpen(false)}
                  variant="secondary"
                  tooltip="Close sidebar"
                />
              </div>
            </div>

            {/* Chat Widget in Sidebar */}
            <div className="mb-6">
              <ChatWidget />
            </div>

            {isAddingPage && (
              <div className="mb-6 p-5 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
                <input
                  type="text"
                  value={newPageTitle}
                  onChange={(e) => setNewPageTitle(e.target.value)}
                  placeholder="New page name"
                  className="w-full p-3 border dark:border-gray-600 rounded-lg mb-3 focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-indigo-500 dark:focus:border-indigo-400 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                <div className="flex gap-2">
                  <Button onClick={handleAddPage} size="sm" disabled={isLoading} loading={isLoading}>
                    Add Page
                  </Button>
                  <Button onClick={() => setIsAddingPage(false)} variant="ghost" size="sm">
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* List of Pages */}
            <div className="space-y-2 overflow-y-auto flex-grow pr-1 custom-scrollbar">
              {isLoading && pages.length === 0 ? (
                <LoadingSpinner />
              ) : (
                pages?.map((page) => (
                  <div
                    key={page._id}
                    className={`group flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                      currentPage === page._id
                        ? "bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-900/20 dark:to-violet-900/20 text-indigo-700 dark:text-indigo-300 border-l-4 border-indigo-500 dark:border-indigo-400"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300"
                    }`}
                    onClick={() => setCurrentPage(page._id)}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div
                        className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                          currentPage === page._id 
                            ? "bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-sm" 
                            : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                        }`}
                      >
                        <span className="text-sm font-medium">{page.title.charAt(0).toUpperCase()}</span>
                      </div>
                      {editingPageId === page._id ? (
                        <input
                          type="text"
                          defaultValue={page.title}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleRenamePage(page._id, e.target.value);
                            }
                          }}
                          onBlur={(e) => handleRenamePage(page._id, e.target.value)}
                          className="bg-white dark:bg-gray-700 border dark:border-gray-600 rounded px-2 py-1 outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 flex-1 min-w-0 text-gray-900 dark:text-gray-100"
                          autoFocus
                        />
                      ) : (
                        <span className="flex-1 truncate font-medium">{page.title}</span>
                      )}
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <IconButton
                        icon={<Edit size={14} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingPageId(page._id);
                        }}
                        variant="secondary"
                        tooltip="Edit page"
                      />
                      {pages.length > 1 && (
                        <IconButton
                          icon={<Trash2 size={14} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete page "${page.title}"?`)) {
                              handleDeletePage(page._id);
                            }
                          }}
                          variant="danger"
                          tooltip="Delete page"
                        />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 overflow-y-auto bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
          <div className="max-w-6xl mx-auto">
            {currentPage === "default" ? (
              <WelcomeMessage />
            ) : (
              <>
                {/* Header with search and "new note" button */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-10 gap-4">
                  <div className="relative">
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 dark:from-indigo-400 dark:to-violet-400 bg-clip-text text-transparent">
                      {currentPageData?.title}
                    </h1>
                    <div className="absolute -bottom-2 left-0 w-1/3 h-1 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-full opacity-70"></div>
                  </div>
                  <div className="flex gap-3 flex-wrap">
                    <div className="relative flex-grow sm:flex-grow-0 max-w-md group">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 group-focus-within:text-indigo-500 dark:group-focus-within:text-indigo-400 transition-colors duration-200">
                        <Search size={18} />
                      </div>
                      <input
                        type="text"
                        placeholder="Search notes..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-10 py-3 w-full rounded-lg border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-sm"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery("")}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
                        >
                          <X size={18} />
                        </button>
                      )}
                    </div>
                    {!isEditing && (
                      <Button
                        onClick={() => {
                          setIsEditing(true);
                          setCurrentNote({
                            id: null,
                            title: "",
                            content: "",
                            attachments: [],
                          });
                        }}
                        icon={<Sparkles size={18} />}
                        size="md"
                        className="shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
                      >
                        New Note
                      </Button>
                    )}
                  </div>
                </div>

                {/* If Editing, show the "Add/Edit Note" card */}
                {isEditing && (
                  <Card className="mb-8 border border-gray-100 dark:border-gray-700 shadow-md hover:shadow-lg transition-all duration-300">
                    <CardContent>
                      <div className="flex items-center mb-6">
                        <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-gradient-to-br from-indigo-500 to-violet-500 rounded-lg shadow-sm text-white mr-4">
                          <PenTool size={20} />
                        </div>
                        <input
                          type="text"
                          value={currentNote.title}
                          onChange={(e) =>
                            setCurrentNote({ ...currentNote, title: e.target.value })
                          }
                          placeholder="Note Title"
                          className="w-full text-xl font-semibold p-3 border-0 border-b border-gray-200 dark:border-gray-700 focus:border-indigo-500 dark:focus:border-indigo-400 focus:ring-0 focus:outline-none bg-transparent text-gray-900 dark:text-gray-100"
                          autoFocus
                        />
                      </div>

                      {/* Editor */}
                      <div className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
                        <div className="bg-gray-50 dark:bg-gray-800 p-2 border-b border-gray-200 dark:border-gray-700 flex items-center">
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-medium flex items-center">
                            <Command size={14} className="mr-1" /> EDITOR
                          </span>
                        </div>
                        <ReactQuill
                          ref={quillRef}
                          value={currentNote.content}
                          onChange={(content) =>
                            setCurrentNote({ ...currentNote, content })
                          }
                          modules={{
                            toolbar: [
                              [{ header: [1, 2, false] }],
                              ["bold", "italic", "underline", "strike"],
                              [{ list: "ordered" }, { list: "bullet" }],
                              ["link", "image"],
                              [{ align: [] }],
                              [{ color: [] }, { background: [] }],
                            ],
                            clipboard: {
                              matchVisual: false,
                            },
                          }}
                          formats={[
                            "header",
                            "bold",
                            "italic",
                            "underline",
                            "strike",
                            "list",
                            "bullet",
                            "link",
                            "image",
                            "align",
                            "color",
                            "background",
                          ]}
                          theme="snow"
                          preserveWhitespace={true}
                          className="min-h-[250px] bg-white dark:bg-gray-700 dark:text-gray-100"
                        />
                      </div>

                      {/* Existing Attachments */}
                      {currentNote.attachments && currentNote.attachments.length > 0 && (
                        <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
                          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 text-gray-700 dark:text-gray-300">
                            <Paperclip size={16} className="text-indigo-500 dark:text-indigo-400" /> 
                            <span>Existing Attachments</span>
                            <span className="ml-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs px-2 py-0.5 rounded-full">
                              {currentNote.attachments.length}
                            </span>
                          </h3>
                          <div className="space-y-3 mt-3">
                            {currentNote.attachments.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg group hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 border border-gray-200 dark:border-gray-700"
                              >
                                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-white dark:bg-gray-700 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600">
                                  {getFileIcon(file.originalName)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <a
                                    href={`${url}/uploads/${file.filename}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block truncate font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
                                    download={file.originalName}
                                  >
                                    {file.originalName}
                                  </a>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center">
                                    <Clock size={12} className="mr-1" /> Added {new Date(file.createdAt || Date.now()).toLocaleDateString()}
                                    <span className="mx-2">•</span>
                                    {(file.size / 1024 / 1024).toFixed(2)} MB
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  <a
                                    href={`${url}/uploads/${file.filename}`}
                                    download={file.originalName}
                                    className="p-2 text-indigo-500 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                    title="Download file"
                                  >
                                    <Download size={16} />
                                  </a>
                                  <button
                                    onClick={() =>
                                      handleDeleteAttachment(currentNote._id, file.filename)
                                    }
                                    className="p-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                    title="Delete attachment"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* New Attachments */}
                      <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold flex items-center gap-2 text-gray-700 dark:text-gray-300">
                            <FileUp size={16} className="text-indigo-500 dark:text-indigo-400" /> 
                            <span>Add Attachments</span>
                          </h3>
                          <Button
                            onClick={() => fileInputRef.current?.click()}
                            variant="secondary"
                            size="sm"
                            icon={<FileUp size={16} />}
                            className="shadow-sm hover:shadow-md transition-all duration-200"
                          >
                            Add Files
                          </Button>
                        </div>

                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                          multiple
                          accept=".pdf,.doc,.docx,.xls,.xlsx"
                          className="hidden"
                        />

                        {/* File Preview List */}
                        <div className="space-y-3 mt-3">
                          {attachments.length > 0 ? (
                            attachments.map((file, index) => (
                              <FileUploadPreview
                                key={index}
                                file={file}
                                onRemove={() => removeAttachment(index)}
                              />
                            ))
                          ) : (
                            <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 text-center group hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 cursor-pointer" onClick={() => fileInputRef.current?.click()}>
                              <div className="w-12 h-12 mx-auto mb-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-full flex items-center justify-center text-indigo-500 dark:text-indigo-400 group-hover:scale-110 transition-transform duration-200">
                                <FileUp size={24} />
                              </div>
                              <p className="text-gray-700 dark:text-gray-300 text-sm font-medium">
                                Drag files here or click to upload
                              </p>
                              <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                                Supported formats: PDF, Word, Excel (Max: 10MB)
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Buttons */}
                      <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <Button
                          onClick={() => {
                            setCurrentNote({ id: null, title: "", content: "", attachments: [] });
                            setIsEditing(false);
                            setAttachments([]);
                          }}
                          variant="ghost"
                          className="px-6"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={() =>
                            currentNote._id ? handleUpdate(currentNote._id) : handleSave()
                          }
                          disabled={isLoading}
                          loading={isLoading}
                          icon={<Save size={18} />}
                          className="px-6 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
                        >
                          {currentNote._id ? "Update" : "Save"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Show list of notes if not editing */}
                {!isEditing && (
                  <>
                    {isLoading && filteredNotes?.length === 0 ? (
                      <LoadingSpinner />
                    ) : filteredNotes?.length === 0 ? (
                      searchQuery ? (
                        // No matches for the search query
                        <div className="text-center py-16">
                          <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                            <Search size={24} className="text-gray-400" />
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-1">
                            No results found
                          </h3>
                          <p className="text-gray-500">Try adjusting your search terms</p>
                        </div>
                      ) : (
                        // No notes at all in this page
                        <EmptyState />
                      )
                    ) : (
                      // Display the notes
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative">
                        {filteredNotes?.map((note) => (
                          <Card
                            key={note._id}
                            className="group hover:shadow-lg transition-all border border-gray-100 dark:border-gray-700 transform hover:-translate-y-1 duration-300"
                            expandable={true}
                          >
                            <CardContent>
                              <div className="flex justify-between items-start mb-3">
                                <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 line-clamp-1">
                                  {note.title}
                                </h2>
                                <div className="flex opacity-0 group-hover:opacity-100 transition-opacity">
                                  <IconButton
                                    icon={<Edit size={16} />}
                                    onClick={() => {
                                      setCurrentNote(note);
                                      setIsEditing(true);
                                      setAttachments([]);
                                    }}
                                    variant="secondary"
                                    tooltip="Edit note"
                                  />
                                  <IconButton
                                    icon={<Trash2 size={16} />}
                                    onClick={() => handleDeleteNote(note._id)}
                                    variant="danger"
                                    tooltip="Delete note"
                                  />
                                  <IconButton
                                    icon={<Maximize size={16} />}
                                    onClick={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setModalPosition({
                                        x: rect.x,
                                        y: rect.y + window.scrollY,
                                      });
                                      setExpandedCard(note._id);
                                    }}
                                    variant="secondary"
                                    tooltip="Expand note"
                                  />
                                </div>
                              </div>

                              {/* Metadata & date */}
                              <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-3">
                                <span className="flex items-center">
                                  <Calendar size={12} className="mr-1" />
                                  {new Date(note.updatedAt || note.createdAt).toLocaleDateString()}
                                </span>
                                {note.attachments?.length > 0 && (
                                  <span className="flex items-center ml-3 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-full">
                                    <Paperclip size={12} className="mr-1" />
                                    {note.attachments.length}
                                  </span>
                                )}
                              </div>

                              {/* Preview content with gradient fade */}
                              <div className="relative h-40 overflow-hidden rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3">
                                <NoteContent
                                  noteId={note._id}
                                  content={note.content}
                                  attachments={[]} // Hide attachments in preview
                                />
                                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50 dark:from-gray-800/90 to-transparent pointer-events-none" />
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal for expanded note */}
      {expandedCard && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-40 z-40 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setExpandedCard(null)}
          />
          <div
            className="fixed z-50 bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-[90vw] max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-gray-100 dark:border-gray-700 transition-all duration-300"
            style={{
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center flex-shrink-0 bg-gray-50 dark:bg-gray-800">
              <div className="flex items-center">
                <div className="w-8 h-8 flex items-center justify-center bg-gradient-to-br from-indigo-500 to-violet-500 rounded-lg shadow-sm text-white mr-3">
                  <FileText size={18} />
                </div>
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                  {filteredNotes.find((n) => n._id === expandedCard)?.title}
                </h2>
              </div>
              <IconButton
                icon={<X size={20} />}
                onClick={() => setExpandedCard(null)}
                variant="secondary"
                tooltip="Close"
              />
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-white dark:bg-gray-800">
              <div className="max-w-full">
                <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-6">
                  <span className="flex items-center">
                    <Calendar size={14} className="mr-1" />
                    {new Date(filteredNotes.find((n) => n._id === expandedCard)?.updatedAt || 
                              filteredNotes.find((n) => n._id === expandedCard)?.createdAt).toLocaleDateString()}
                  </span>
                  <span className="mx-2">•</span>
                  <span className="flex items-center">
                    <User size={14} className="mr-1" />
                    {user?.firstName || "You"}
                  </span>
                </div>
                <NoteContent
                  noteId={expandedCard}
                  content={filteredNotes.find((n) => n._id === expandedCard)?.content}
                  attachments={filteredNotes.find((n) => n._id === expandedCard)?.attachments}
                  onDeleteAttachment={handleDeleteAttachment}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 flex-shrink-0 bg-gray-50 dark:bg-gray-800">
              <Button
                variant="secondary"
                onClick={() => {
                  const note = filteredNotes.find((n) => n._id === expandedCard);
                  setCurrentNote(note);
                  setIsEditing(true);
                  setAttachments([]);
                  setExpandedCard(null);
                }}
                icon={<Edit size={16} />}
                className="shadow-sm hover:shadow-md transition-all duration-200"
              >
                Edit Note
              </Button>
              <Button variant="ghost" onClick={() => setExpandedCard(null)}>
                Close
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotesAurora;
